
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { ArrowLeft, BookOpen, Code, Layers, Shield, Menu, X, ChevronLeft, ChevronRight, HelpCircle, Package, Server, Palette, Star, LayoutDashboard, Smartphone, FileCode, Cpu, Link2, Globe, Coins, Milestone, Users, Briefcase, Hash, Moon, Sun } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/Button';
import { useTheme } from '../../context/ThemeContext';
import { ApiReference } from './content/ApiReference';
import { Introduction } from './content/Introduction';
import { Terminology } from './content/Terminology';
import { Architecture } from './content/Architecture';
import { PaymentFlow } from './content/PaymentFlow';
import { ProductsInventory } from './content/ProductsInventory';
import { ApiSdks } from './content/ApiSdks';
import { BrandGuide } from './content/BrandGuide';
import { AboutGhostPay } from './content/AboutGhostPay';
import { CoreFeatures } from './content/CoreFeatures';
import { GhostWalletMobile } from './content/GhostWalletMobile';
import { Dashboard } from './content/Dashboard';
import { ContractsAndPrograms } from './content/ContractsAndPrograms';
import { Technologies } from './content/Technologies';
import { IntegrationsDoc } from './content/IntegrationsDoc';
import { StorefrontDoc } from './content/StorefrontDoc';
import { GhostTokenSuiteDoc } from './content/GhostTokenSuite';
import { RoadmapDoc } from './content/Roadmap';
import { UsersDoc } from './content/UsersDoc';
import { UseCasesDoc } from './content/UseCasesDoc';

interface DocsViewerProps {
    onBack: () => void;
    isPublic?: boolean;
    initialPage?: string;
}

const CATEGORIES = [
    {
        label: 'Getting Started',
        pages: ['introduction', 'about-ghostpay', 'users', 'use-cases', 'roadmap']
    },
    {
        label: 'Protocol',
        pages: ['ghost-token-suite', 'core-features', 'technologies', 'integrations']
    },
    {
        label: 'Interfaces',
        pages: ['mobile-app', 'dashboard', 'storefront']
    },
    {
        label: 'Technical',
        pages: ['terminology', 'architecture', 'contracts', 'payment-flow', 'products-inventory']
    },
    {
        label: 'Reference',
        pages: ['api-sdks', 'api-reference', 'brand-guide']
    }
];

const SECTIONS = [
    { id: 'introduction', label: 'Introduction', icon: BookOpen, component: <Introduction /> },
    { id: 'about-ghostpay', label: 'About GhostPay', icon: HelpCircle, component: <AboutGhostPay /> },
    { id: 'users', label: 'Target Users', icon: Users, component: <UsersDoc /> },
    { id: 'use-cases', label: 'Use Cases', icon: Briefcase, component: <UseCasesDoc /> },
    { id: 'roadmap', label: 'Roadmap', icon: Milestone, component: <RoadmapDoc /> },
    { id: 'ghost-token-suite', label: 'Ghost Token Suite', icon: Coins, component: <GhostTokenSuiteDoc /> },
    { id: 'core-features', label: 'Core Features', icon: Star, component: <CoreFeatures /> },
    { id: 'technologies', label: 'Technologies', icon: Cpu, component: <Technologies /> },
    { id: 'integrations', label: 'Integrations', icon: Link2, component: <IntegrationsDoc /> },
    { id: 'mobile-app', label: 'GhostWallet Mobile', icon: Smartphone, component: <GhostWalletMobile /> },
    { id: 'dashboard', label: 'Dashboard Guide', icon: LayoutDashboard, component: <Dashboard /> },
    { id: 'storefront', label: 'Storefront', icon: Globe, component: <StorefrontDoc /> },
    { id: 'terminology', label: 'Terminology', icon: HelpCircle, component: <Terminology /> },
    { id: 'architecture', label: 'System Architecture', icon: Layers, component: <Architecture /> },
    { id: 'contracts', label: 'Contracts & Programs', icon: FileCode, component: <ContractsAndPrograms /> },
    { id: 'payment-flow', label: 'Payment & Validation', icon: Code, component: <PaymentFlow /> },
    { id: 'products-inventory', label: 'Products & Inventory', icon: Package, component: <ProductsInventory /> },
    { id: 'api-sdks', label: 'API SDKs', icon: Server, component: <ApiSdks /> },
    { id: 'api-reference', label: 'API Reference', icon: Shield, component: <ApiReference /> },
    { id: 'brand-guide', label: 'Brand Guide', icon: Palette, component: <BrandGuide /> },
];

export const DocsViewer: React.FC<DocsViewerProps> = ({ onBack, isPublic = false, initialPage = 'introduction' }) => {
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const { theme, toggleTheme } = useTheme();

    const { currentSection, prevSection, nextSection } = useMemo(() => {
        const index = SECTIONS.findIndex(s => s.id === currentPage);
        return {
            currentSection: SECTIONS[index] || SECTIONS[0],
            prevSection: index > 0 ? SECTIONS[index - 1] : null,
            nextSection: index < SECTIONS.length - 1 ? SECTIONS[index + 1] : null,
        };
    }, [currentPage]);

    const SidebarNav = () => (
        <div className="space-y-6">
            <div className="px-4 mb-4">
                <h2 className="text-xs font-bold text-stone-900 dark:text-white uppercase tracking-[0.2em] flex items-center gap-2">
                    <Hash size={14} className="text-nobel-gold" />
                    Documentation
                </h2>
            </div>
            {CATEGORIES.map(cat => (
                <div key={cat.label} className="space-y-1">
                    <h3 className="px-4 text-[9px] font-bold text-stone-400 dark:text-stone-500 uppercase tracking-widest mb-1.5">{cat.label}</h3>
                    <div className="space-y-0.5">
                        {cat.pages.map(pageId => {
                            const section = SECTIONS.find(s => s.id === pageId);
                            if (!section) return null;
                            const isActive = currentPage === pageId;
                            return (
                                <button 
                                    key={pageId} 
                                    onClick={() => {
                                        setCurrentPage(pageId);
                                        setIsSidebarOpen(false);
                                        const mainContent = document.getElementById('docs-main-content');
                                        if (mainContent) mainContent.scrollTo({ top: 0, behavior: 'smooth' });
                                        else window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    className={`w-full flex items-center gap-3 px-4 py-1.5 rounded-lg text-[13px] transition-all text-left group ${isActive ? 'bg-stone-100 dark:bg-white/5 text-nobel-gold dark:text-nobel-gold font-bold' : 'text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200'}`}
                                >
                                    <section.icon size={13} className={`${isActive ? 'opacity-100' : 'opacity-30 group-hover:opacity-60'}`} />
                                    <span className="truncate">{section.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="flex flex-col h-full lg:max-h-[calc(100vh-12rem)]">
            {/* Docs Header */}
            <div className="flex items-center justify-between gap-4 mb-6 pb-4 border-b border-stone-100 dark:border-white/5 shrink-0">
                <div className="flex items-center gap-4">
                    <Button variant="outline" size="sm" onClick={onBack} className="!p-0 h-8 w-8 rounded-full border-stone-200 dark:border-white/10">
                        <ArrowLeft size={14} />
                    </Button>
                    <div>
                        <h1 className="font-serif text-xl md:text-2xl text-stone-900 dark:text-white font-bold tracking-tight leading-none">Ghost Docs</h1>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={toggleTheme}
                        className="p-2 rounded-lg bg-stone-100 dark:bg-white/5 text-stone-600 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-white/10 transition-colors"
                        title="Toggle Dark Mode"
                    >
                        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                    </button>
                    <button 
                        onClick={() => setIsSidebarOpen(true)} 
                        className="lg:hidden p-2 rounded-lg bg-stone-100 dark:bg-white/5 text-stone-600 dark:text-stone-300"
                    >
                        <Menu size={18} />
                    </button>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden gap-8 lg:gap-12 relative">
                {/* Desktop Sidebar */}
                <aside className="hidden lg:block w-56 shrink-0 border-r border-stone-100 dark:border-white/5 overflow-y-auto no-scrollbar pb-10">
                    <SidebarNav />
                </aside>

                {/* Main Docs Content Area */}
                <main id="docs-main-content" className="flex-1 overflow-y-auto no-scrollbar pb-20 px-1">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentPage}
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            className="max-w-4xl"
                        >
                            <header className="mb-10 space-y-4">
                                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-nobel-gold/5 text-nobel-gold text-[9px] font-bold uppercase tracking-[0.2em] border border-nobel-gold/10">
                                    {CATEGORIES.find(c => c.pages.includes(currentPage))?.label || 'General'}
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-stone-900 dark:bg-white/5 rounded-2xl text-nobel-gold dark:text-nobel-gold shadow-sm border border-white/5">
                                        <currentSection.icon size={24} strokeWidth={1.5} />
                                    </div>
                                    <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#0A0B0F] dark:text-white tracking-tight leading-tight">{currentSection.label}</h2>
                                </div>
                            </header>

                            <div className="prose dark:prose-invert max-w-none text-stone-600 dark:text-stone-300 leading-relaxed font-light text-sm md:text-base selection:bg-nobel-gold/20">
                                {currentSection.component}
                            </div>

                            {/* Pagination Buttons */}
                            <div className="mt-16 flex flex-col sm:flex-row justify-between gap-4 border-t border-stone-100 dark:border-white/5 pt-8">
                                {prevSection ? (
                                    <button 
                                        onClick={() => {
                                            setCurrentPage(prevSection.id);
                                            document.getElementById('docs-main-content')?.scrollTo({ top: 0, behavior: 'smooth' });
                                        }} 
                                        className="flex items-center gap-3 p-4 rounded-xl border border-stone-200 dark:border-white/10 hover:border-nobel-gold/40 hover:bg-stone-50 dark:hover:bg-white/[0.01] transition-all group text-left flex-1"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-stone-100 dark:bg-white/5 flex items-center justify-center text-stone-400 group-hover:text-nobel-gold transition-colors shrink-0">
                                            <ChevronLeft size={16} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[9px] text-stone-400 uppercase tracking-widest font-bold mb-0.5">Previous</div>
                                            <div className="text-xs font-bold text-stone-900 dark:text-white truncate">{prevSection.label}</div>
                                        </div>
                                    </button>
                                ) : <div className="flex-1" />}

                                {nextSection ? (
                                    <button 
                                        onClick={() => {
                                            setCurrentPage(nextSection.id);
                                            document.getElementById('docs-main-content')?.scrollTo({ top: 0, behavior: 'smooth' });
                                        }} 
                                        className="flex items-center justify-end gap-3 p-4 rounded-xl border border-stone-200 dark:border-white/10 hover:border-nobel-gold/40 hover:bg-stone-50 dark:hover:bg-white/[0.01] transition-all group text-right flex-1"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-[9px] text-stone-400 uppercase tracking-widest font-bold mb-0.5">Next</div>
                                            <div className="text-xs font-bold text-stone-900 dark:text-white truncate">{nextSection.label}</div>
                                        </div>
                                        <div className="w-8 h-8 rounded-full bg-stone-100 dark:bg-white/5 flex items-center justify-center text-stone-400 group-hover:text-nobel-gold transition-colors shrink-0">
                                            <ChevronRight size={16} />
                                        </div>
                                    </button>
                                ) : <div className="flex-1" />}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </main>
            </div>

            {/* Mobile Navigation Drawer */}
            <AnimatePresence>
                {isSidebarOpen && (
                    <motion.div 
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
                        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm lg:hidden" 
                        onClick={() => setIsSidebarOpen(false)}
                    >
                        <motion.div 
                            initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }} 
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }} 
                            onClick={e => e.stopPropagation()} 
                            className="fixed top-0 left-0 h-full w-[80%] max-w-xs bg-white dark:bg-[#0A0B0F] shadow-2xl flex flex-col"
                        >
                            <div className="p-5 flex justify-between items-center border-b border-stone-100 dark:border-white/5">
                                <h2 className="font-serif text-lg font-bold text-stone-900 dark:text-white">Guide</h2>
                                <button onClick={() => setIsSidebarOpen(false)} className="p-2 text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors"><X size={20}/></button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-5 no-scrollbar pb-12">
                                <SidebarNav />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
